import { Config } from 'config/envConfig';
import { useState, useEffect, useCallback } from 'react';
import {
    Button,
    ConfirmModal,
    Modal,
    alert,
    AlertType,
    LoadingView,
} from '@quyva/shared-ui';
import { Table, Space } from 'antd';
import { faPencil, faTrash, faWrench } from '@fortawesome/free-solid-svg-icons';
import AddInstanceDialog from '../toolsSection/AddInstanceDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUrl } from '../pdfDisplay/utils.js';
import axios from 'axios';
import { getDate, getTime, toSentenceCase } from '@quyva/common';

export default function ToolsMainView({
    user,
    allTools,
    selectedToolId,
    setSelectedToolId,
    showAddInstanceDialog,
    setShowAddInstanceDialog,
    activeTool,
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [instances, setInstances] = useState([]);
    const [instanceToDelete, setInstanceToDelete] = useState(null);
    const [editingInstance, setEditingInstance] = useState(null);

    // Extract fetch logic into a reusable function
    const fetchInstances = useCallback(async () => {
        setIsLoading(true);
        setLoadingText('Loading tools data...');
        try {
            const response = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/tool_manager/get_tool_instances/${user.email}`
                )
            );

            if (response.data && response.data.instances) {
                setInstances(response.data.instances || []);
                return response.data.instances || [];
            }
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to load tools data.');
            console.error('Error fetching tools data:', err);
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    }, [user]);

    useEffect(() => {
        fetchInstances();
    }, [
        fetchInstances,
        showAddInstanceDialog,
        editingInstance,
        instanceToDelete,
    ]);

    // --- Instance Management Functions ---

    const handleCreateInstance = async (toolId, instanceData) => {
        // console.log('Creating instance for tool:', toolId);
        // console.log(toolId, instanceData);

        setIsLoading(true);
        setLoadingText(`Creating instance "${instanceData.instance_name}"...`);
        setSelectedToolId(toolId);
        try {
            const formData = new FormData();
            formData.append('instance_name', instanceData.instance_name);
            formData.append('tool_name', toolId);
            formData.append('owner_id', user.email);
            formData.append('config', JSON.stringify(instanceData.config)); // must stringify
            formData.append('custom_description', instanceData.description);
            if (instanceData.file) {
                const fileFieldName = Object.keys(instanceData.file)[0]; // Get the field name for the file
                formData.append(
                    'file_upload',
                    instanceData.file[fileFieldName]
                );
            }

            const response = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/tool_manager/add_new_instance`
                ),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data && response.data.status === 'success') {
                alert(AlertType.SUCCESS, 'New instance added successfully');
                setShowAddInstanceDialog(false);
                await fetchInstances();
            }
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to add new instance.');
            console.error('Error adding instance:', err);
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    };

    const handleUpdateInstance = async (instanceId, updatedData) => {
        setIsLoading(true);
        setLoadingText(`Updating instance "${updatedData.instance_name}"...`);
        try {
            // console.log('Updating instance:', instanceId, updatedData);
            // console.log(instanceId);
            // console.log(updatedData.config);
            // console.log(updatedData.description);
            const formData = new FormData();
            formData.append('instance_id', instanceId);
            formData.append('new_config', JSON.stringify(updatedData.config));
            formData.append('new_description', updatedData.description);
            if (updatedData.file) {
                const fileFieldName = Object.keys(updatedData.file)[0];
                formData.append('file_upload', updatedData.file[fileFieldName]);
            }

            const response = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/tool_manager/update_instance`
                ),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data) {
                alert(AlertType.SUCCESS, 'Instance updated successfully');
                setInstances((prev) =>
                    prev.map((inst) =>
                        inst.id === instanceId
                            ? { ...inst, ...updatedData }
                            : inst
                    )
                );
                // console.log("updated",instances);
            }

            setEditingInstance(null); // Close the dialog after successful update
            await fetchInstances();
        } catch (err) {
            alert(
                AlertType.ERROR,
                `Error updating "${updatedData.instance_name}".`
            );
            console.error('Error updating instance:', err);
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    };

    const handleDeleteInstance = async () => {
        if (!instanceToDelete) return; // Should not happen
        setIsLoading(true);
        setLoadingText(
            `Deleting instance "${instanceToDelete.instance_name}"...`
        );
        try {
            const formData = new FormData();
            formData.append('instance_id', instanceToDelete.id);
            const response = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/tool_manager/delete_instance`
                ),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data) {
                alert(
                    AlertType.SUCCESS,
                    `Tool instance "${instanceToDelete.instance_name}" deleted successfully!`
                );
            }
            setInstanceToDelete(null); // Close the confirm modal
        } catch (err) {
            alert(
                AlertType.ERROR,
                `Error deleting "${instanceToDelete.instance_name}".`
            );
            console.error('Error deleting instance:', err);
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    };

    // --- Filtered Instances for Display ---
    const displayedInstances =
        selectedToolId === 'all'
            ? instances
            : instances.filter((i) => i.tool_name === selectedToolId);

    const truncateWords = (text, wordLimit) => {
        if (!text) return '';
        const words = text.split(' ');
        return words.length > wordLimit
            ? words.slice(0, wordLimit).join(' ') + '...'
            : text;
    };

    const handleEditClick = async (record) => {
        const freshInstances = await fetchInstances();
        const freshInstance = freshInstances.find((i) => i.id === record.id);
        if (freshInstance) setEditingInstance(freshInstance);
    };


    // --- Ant Design Table Columns ---
    const columns = [
        {
            title: 'INSTANCE NAME',
            dataIndex: 'instance_name',
            key: 'instance_name',
        },
        {
            title: 'TOOL NAME',
            dataIndex: 'tool_name',
            key: 'tool_name',
            render: (text) => toSentenceCase(text),
        },
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            render: (text) => (
                <span title={text}>
                    {' '}
                    {truncateWords(text.trim().replace(/\s+/g, ' '), 4)}
                </span>
            ),
        },
        {
            title: 'UPDATED AT',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (text) =>
                text ? (
                    <>
                        <span className='mr-2'>{getDate(text)}</span>
                        <span>{getTime(text)}</span>
                    </>
                ) : (
                    '-'
                ),
        },
        {
            title: 'ACTIONS',
            key: 'actions',
            render: (_, record) =>
                record.isSystemDefault ? (
                    <span className='text-texts-secondary300 text-sm'>
                        Shared default - read only
                    </span>
                ) : (
                    <Space size='middle'>
                        <Button
                            icon={faPencil}
                            onClick={() => handleEditClick(record)}
                            hoverText={'Edit Instance'}
                            type={'warning'}
                        />
                        <Button
                            icon={faTrash}
                            onClick={() => setInstanceToDelete(record)}
                            hoverText={'Delete Instance'}
                            type={'danger'}
                        />
                    </Space>
                ),
        },
    ];

    // --- Loading State Render ---
    if (isLoading) {
        return (
            <div className='w-full h-full flex justify-center items-center'>
                <LoadingView loadingText={loadingText} inLine={false} />
            </div>
        );
    }

    // --- Main Render ---
    return (
        <div className='p-4'>
            {/* Display "No Instances" message only if specifically filtered tool has no instances */}
            {displayedInstances.length === 0 ? (
                <div className='flex flex-col w-full h-[78vh] items-center justify-center text-texts-secondary300'>
                    <FontAwesomeIcon
                        icon={faWrench}
                        className='w-[8vw] h-[8vw] mb-4 text-icons-primary'
                    />
                    <h1 className='text-2xl font-semibold'>
                        {selectedToolId === 'all'
                            ? 'No Instances Found'
                            : `No Instances Found for "${toSentenceCase(
                                allTools.find((t) => t.id === selectedToolId)
                                    ?.name || selectedToolId
                            )}"`}
                    </h1>
                    <p className='text-lg mt-2'>
                        Create a new instance to get started.
                    </p>
                </div>
            ) : (
                <Table
                    dataSource={displayedInstances}
                    columns={columns}
                    rowKey='id'
                    pagination={{ pageSize: 7 }}
                />
            )}

            {showAddInstanceDialog && (
                <AddInstanceDialog
                    open={showAddInstanceDialog}
                    onClose={() => setShowAddInstanceDialog(false)}
                    onCreate={handleCreateInstance}
                    tools={allTools}
                    activeTool={activeTool}
                    mode='add'
                    user={user}
                    instances={instances}
                />
            )}

            {editingInstance && (
                <Modal
                    title={`Edit Instance: ${editingInstance.instance_name}`}
                    titleIcon={faWrench}
                    onClose={() => setEditingInstance(null)}
                >
                    <AddInstanceDialog
                        open={!!editingInstance}
                        onClose={() => setEditingInstance(null)}
                        onUpdate={handleUpdateInstance}
                        tools={allTools}
                        mode='edit'
                        initialData={editingInstance}
                        user={user}
                        instances={instances}
                    />
                </Modal>
            )}

            {instanceToDelete && (
                <ConfirmModal
                    modalText={`Are you sure you want to delete "${instanceToDelete?.instance_name}" instance?`}
                    onConfirm={handleDeleteInstance}
                    onCancel={() => setInstanceToDelete(null)}
                />
            )}
        </div>
    );
}
